import { UserRole } from '#enums';
import { UserDetails } from '#models';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _msalService = inject(MsalService);
  private _msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);

  /** Interactive authentication uses redirects exclusively; silent token acquisition does not set these statuses. */
  readonly redirecting = toSignal(inject(MsalBroadcastService).inProgress$.pipe(
    map(status => status === InteractionStatus.Login || status === InteractionStatus.AcquireToken),
  ), { initialValue: false });

  private _userDetails$ = new BehaviorSubject<UserDetails | null>(null);

  get userDetails$() {
    return this._userDetails$.asObservable();
  }

  get isAdmin$() {
    return this.userDetails$.pipe(map(d => d?.isAdmin ?? false));
  }

  setUserDetails(userDetails: UserDetails | null) {
    this._userDetails$.next(userDetails);
  }

  getUserDetails() {
    return this._userDetails$.getValue();
  }

  isAuthorized(roles: UserRole[] = []): boolean {
    const userDetails = this._userDetails$.getValue();
    if (!userDetails) {
      return false;
    }

    if (roles.includes(UserRole.User)) {
      return userDetails.hasAccess;
    }

    if (roles.includes(UserRole.Admin)) {
      return userDetails.isAdmin;
    }

    return false;
  }

  isAuthenticated() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0;
  }

  getAccount() {
    const accounts = this._msalService.instance.getAllAccounts();
    return accounts && accounts.length > 0 ? accounts[0] : null;
  }

  getRoles() {
    const account = this.getAccount();
    if (!account) {
      return [];
    }

    const idTokenClaims = account.idTokenClaims as { roles?: string[] };
    return idTokenClaims.roles || [];
  }

  isAdmin(): boolean {
    const roles = this.getRoles();
    return roles.includes(UserRole.Admin);
  }

  getAccountEmail() {
    return this.getAccount()?.username || '';
  }

  /** The signed-in user's Entra object id, the identity the workflow engine records as owner. */
  getObjectId(): string | null {
    const account = this.getAccount();
    if (!account) {
      return null;
    }
    return (account.idTokenClaims as { oid?: string } | undefined)?.oid ?? account.localAccountId ?? null;
  }

  initialize(): Promise<void> {
    return firstValueFrom(this._msalService.handleRedirectObservable()).then(async result => {

      if (result !== null) {
        // User just completed login redirect
        this._msalService.instance.setActiveAccount(result.account);
        return;
      }

      // Check if user already has an active session
      if (this.isAuthenticated()) {
        const account = this.getAccount();
        this._msalService.instance.setActiveAccount(account);
        return;
      }

      return firstValueFrom(this._msalService.loginRedirect(this._msalGuardConfig.authRequest as RedirectRequest));
    });
  }
}
